import { Checkbox } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronRight, Folder, Package } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useProductsCatalog } from '../hooks';
import {
  BULK_MAX_PRODUCTS,
  groupCatalogProducts,
  resolveGroupCheckboxState,
  toggleGroupSelection,
} from '../utils/bulk-broker-products.util';

interface BulkProductsProductPickerProps {
  selected: Set<string>;
  onChange: (_next: Set<string>) => void;
  disabled?: boolean;
}

export function BulkProductsProductPicker({
  selected,
  onChange,
  disabled = false,
}: BulkProductsProductPickerProps) {
  const { data, isLoading, error } = useProductsCatalog();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const groups = useMemo(() => groupCatalogProducts(data ?? []), [data]);

  const toggleProduct = (code: string) => {
    const next = new Set(selected);

    if (next.has(code)) {
      next.delete(code);
    } else {
      next.add(code);
    }

    onChange(next);
  };

  if (error) {
    return (
      <section className='flex flex-col gap-2'>
        <SectionTitle />
        <p className='text-sm text-alba-error-red-500'>
          Erro ao carregar o catálogo de produtos.
        </p>
      </section>
    );
  }

  return (
    <section className='flex min-w-0 flex-col gap-3'>
      <SectionTitle />

      {isLoading ? (
        <p className='text-sm text-alba-gray-600'>Carregando produtos…</p>
      ) : (
        <div className='flex flex-col gap-1 rounded-xl border border-alba-gray-200 p-2'>
          {groups.map(group => {
            // Grupos começam abertos: são dois ou três, não vinte.
            const isCollapsed = collapsed[group.key] ?? false;
            const groupState = resolveGroupCheckboxState(group, selected);

            return (
              <div key={group.key} className='flex flex-col'>
                <div className='flex items-center gap-2 py-1'>
                  <button
                    type='button'
                    onClick={() =>
                      setCollapsed(prev => ({
                        ...prev,
                        [group.key]: !isCollapsed,
                      }))
                    }
                    className='flex h-6 w-6 items-center justify-center rounded-md text-alba-gray-500 hover:bg-alba-gray-200'
                    aria-label={isCollapsed ? 'Expandir' : 'Recolher'}
                  >
                    {isCollapsed ? (
                      <ChevronRight className='h-4 w-4' strokeWidth={1.6} />
                    ) : (
                      <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
                    )}
                  </button>

                  {/* `indeterminate` e prop propria, nao um valor de `checked`.
                      E o HiddenInput e obrigatorio: sem ele o Ark nao dispara
                      onCheckedChange e o clique nao faz nada. */}
                  <Checkbox.Root
                    size='small'
                    checked={groupState === true}
                    indeterminate={groupState === 'indeterminate'}
                    disabled={disabled}
                    onCheckedChange={() =>
                      onChange(toggleGroupSelection(group, selected))
                    }
                  >
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                    <Checkbox.HiddenInput />
                  </Checkbox.Root>

                  <Folder
                    className='h-5 w-5 text-alba-gray-500'
                    strokeWidth={1.6}
                  />
                  <span className='text-base text-alba-gray-700'>
                    {group.label}
                  </span>
                </div>

                {!isCollapsed && (
                  <div className='ml-8 flex flex-col border-l border-alba-gray-200 pl-4'>
                    {group.products.map(product => (
                      <div
                        key={product.code}
                        role='button'
                        tabIndex={-1}
                        onClick={() => !disabled && toggleProduct(product.code)}
                        className='flex cursor-pointer items-center gap-2 py-1'
                      >
                        <span onClick={event => event.stopPropagation()}>
                          <Checkbox.Root
                            size='small'
                            checked={selected.has(product.code)}
                            disabled={disabled}
                            onCheckedChange={() => toggleProduct(product.code)}
                            aria-label={`Selecionar ${product.name}`}
                          >
                            <Checkbox.Control>
                              <Checkbox.Indicator />
                            </Checkbox.Control>
                            <Checkbox.HiddenInput />
                          </Checkbox.Root>
                        </span>
                        <span className='min-w-0 flex-1 truncate text-sm text-alba-gray-700'>
                          {product.name}
                        </span>
                        <span className='text-xs text-alba-gray-500'>
                          {product.code}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className='text-xs text-alba-gray-600'>
        Pacote Telefônica e produtos de teste não entram na edição em lote —
        ajuste pela tela de cada corretora.
      </p>

      {selected.size > BULK_MAX_PRODUCTS && (
        <p className='text-xs text-alba-error-red-500'>
          Máximo de {BULK_MAX_PRODUCTS} produtos por operação.
        </p>
      )}
    </section>
  );
}

function SectionTitle() {
  return (
    <header className='flex items-center gap-2'>
      <Package className='h-4 w-4 text-alba-gray-600' strokeWidth={1.8} />
      <h3 className='text-sm font-bold uppercase tracking-[0.04em] text-alba-gray-600'>
        2. Produtos
      </h3>
    </header>
  );
}
